<template>
  <VueFinalModal overlay-transition="vfm-fade" content-transition="vfm-fade">
    <div class="layout">
      <div class="ConfirmDialog">
        <ScrollOverflow class="scroll" is-vertical>
          <div class="header">
            {{ title || 'Are you sure?' }}
          </div>

          <div class="options">
            {{ prompt }}
          </div>

          <div class="actions">
            <GamButton
              v-if="onCancel"
              :variant="GamButtonVariant.STEKLO"
              :size="GamButtonSize.SMALL"
              @click="onCancel"
              :is-disabled="isSubmitting"
            >
              {{ safeCancelLabel }}
            </GamButton>

            <GamButton
              :variant="GamButtonVariant.HYPE"
              :size="GamButtonSize.SMALL"
              @click="handleConfirm"
              :is-loading="isSubmitting"
            >
              {{ safeConfirmLabel }}
            </GamButton>
          </div>
        </ScrollOverflow>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup lang="ts">
import GamButton from '@/views/components/GamButton.vue';
import ScrollOverflow from '@/views/components/ScrollOverflow.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { computed, ref } from 'vue';
import { VueFinalModal } from 'vue-final-modal';

interface ConfirmDialogProps {
  title?: string;
  prompt: string;
  onCancel?: () => Promise<void>;
  cancelLabel?: string;
  onConfirm: () => Promise<void>;
  confirmLabel?: string;
}

const props = defineProps<ConfirmDialogProps>();

const isSubmitting = ref<boolean>(false);

const handleConfirm = async () => {
  isSubmitting.value = true;

  await props.onConfirm();

  isSubmitting.value = false;
};

const safeCancelLabel = computed(() => {
  if (props.cancelLabel) {
    return props.cancelLabel;
  }

  return 'Cancel';
});

const safeConfirmLabel = computed(() => {
  if (props.confirmLabel) {
    return props.confirmLabel;
  }

  return props.onCancel ? 'Confirm' : 'Okay';
});
</script>

<style lang="scss" scoped>
.layout {
  width: 100svw;
  height: 100svh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ConfirmDialog {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  // padding: 2rem;
  margin: 2rem;
  max-height: 600px;
  width: 100%;
  max-width: 24rem;
  background-color: var(--color-dark-600);
  border-radius: var(--radius-small);
  overflow: hidden;
}

.header {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--color-dark-600);
  border-bottom: 1px solid var(--color-white-10);
  z-index: 2;
}

.actions {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  background: var(--color-dark-600);
  z-index: 2;
}

.scroll {
  width: 100%;
}

.options {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 1rem;
  padding: 1.5rem 2.5rem;
}
</style>
